import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Phone, Mail, Clock, AlertCircle, Lock, CheckCircle, Save, Instagram, Facebook, ChevronDown, UploadCloud, X, Sparkles, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_BASE, getImageUrl } from '../config';

// Sub-components
import AdminBookings from './admin/AdminBookings';
import AdminServices from './admin/AdminServices';
import AdminGallery from './admin/AdminGallery';
import AdminStats from './admin/AdminStats';
import DeleteConfirmationModal from './common/DeleteConfirmationModal';

export default function AdminPanel({ t, user, lang }) {
  const { translations: globalTranslations, refreshTranslations } = useLanguage();
  const [activeTab, setActiveTab] = useState('bookings'); 
  
  // Data States
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [contactInfo, setContactInfo] = useState({
    phone: '', email: '', address: '', hours: '', instagram: '', facebook: ''
  });
  
  const [aboutInfo, setAboutInfo] = useState({ about_image: '', hero_image: '', logo_image: '' });
  const [aboutTexts, setAboutTexts] = useState({ fr: '', en: '', pt: '' });
  
  // Images upload for settings
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [logoImageFile, setLogoImageFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const heroFileInputRef = useRef(null);
  const logoFileInputRef = useRef(null);
  
  // Translation Editing State
  const [editingLang, setEditingLang] = useState('fr');
  const [editingSection, setEditingSection] = useState('nav');
  const [translationValues, setTranslationValues] = useState({});

  // Delete Confirmation State
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  // Account/Password State
  const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordMessage, setPasswordMessage] = useState(null);
  
  // Initialisation des valeurs de traduction
  useEffect(() => {
    if (activeTab === 'translations' && globalTranslations) {
        const flatten = (obj, prefix = '') => {
            return Object.keys(obj).reduce((acc, k) => {
              const pre = prefix.length ? prefix + '.' : '';
              if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k]))
                Object.assign(acc, flatten(obj[k], pre + k));
              else
                acc[pre + k] = obj[k];
              return acc;
            }, {});
        };
        
        const sectionData = globalTranslations[editingLang] && globalTranslations[editingLang][editingSection] 
            ? { [editingSection]: globalTranslations[editingLang][editingSection] } 
            : {};
            
        setTranslationValues(flatten(sectionData));
    }
    
    if (activeTab === 'about' && globalTranslations) {
        setAboutTexts({
            fr: globalTranslations.fr?.about?.text || '',
            en: globalTranslations.en?.about?.text || '',
            pt: globalTranslations.pt?.about?.text || ''
        });
    }
  }, [activeTab, editingLang, editingSection, globalTranslations]);

  const fetchData = useCallback(async () => {
    if (activeTab === 'account' || activeTab === 'translations' || activeTab === 'stats') return;

    try {
      if (activeTab === 'bookings') {
        const res = await fetch(`${API_BASE}/bookings`);
        setBookings(await res.json());
      } else if (activeTab === 'services') {
        const res = await fetch(`${API_BASE}/services`);
        setServices(await res.json());
      } else if (activeTab === 'gallery') {
        const res = await fetch(`${API_BASE}/gallery`);
        setGalleryItems(await res.json());
      } else if (activeTab === 'contact' || activeTab === 'about') {
        const res = await fetch(`${API_BASE}/settings`);
        const data = await res.json();
        
        if (activeTab === 'contact') {
          setContactInfo({
            phone: data.phone || '', email: data.email || '', address: data.address || '',
            hours: data.hours || '', instagram: data.instagram || '', facebook: data.facebook || ''
          });
        } else {
          setAboutInfo({
            about_image: data.about_image || '',
            hero_image: data.hero_image || '', logo_image: data.logo_image || ''
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const requestDelete = (id, type) => setDeleteConfirmation({ id, type });

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    const { id, type } = deleteConfirmation;
    try {
      await fetch(`${API_BASE}/${type}/${id}`, { method: 'DELETE' });
      if (type === 'services') setServices(prev => prev.filter(item => item.id !== id));
      else if (type === 'bookings') setBookings(prev => prev.filter(item => item.id !== id));
      else if (type === 'gallery') setGalleryItems(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
        setDeleteConfirmation(null);
    }
  };

  const handleConfirmBooking = async (id) => {
    await fetch(`${API_BASE}/bookings/${id}/confirm`, { method: 'PUT' });
    fetchData();
  };

  const handleSaveService = async (serviceData) => {
    const url = serviceData.id ? `${API_BASE}/services/${serviceData.id}` : `${API_BASE}/services`;
    await fetch(url, {
      method: serviceData.id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(serviceData)
    });
    fetchData();
  };

  const handleGalleryUpload = async (file) => {
    const formData = new FormData();
    formData.append('media_file', file);
    await fetch(`${API_BASE}/gallery`, { method: 'POST', body: formData });
    fetchData();
  };

  const handleSaveAbout = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    if (aboutImageFile) formData.append('about_image_file', aboutImageFile);
    if (heroImageFile) formData.append('hero_image_file', heroImageFile);
    if (logoImageFile) formData.append('logo_image_file', logoImageFile);
    
    if (aboutInfo.logo_image === '' && !logoImageFile) formData.append('logo_image', '');
    if (aboutInfo.hero_image === '' && !heroImageFile) formData.append('hero_image', '');
    if (aboutInfo.about_image === '' && !aboutImageFile) formData.append('about_image', '');
    
    try {
        await fetch(`${API_BASE}/settings`, { method: 'POST', body: formData });
        
        for (const l of ['fr', 'en', 'pt']) {
            await fetch(`${API_BASE}/translations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lang: l,
                    updates: { 'about.text': aboutTexts[l] }
                })
            });
        }

        alert(t.admin?.common?.success || 'Enregistré !');
        setAboutImageFile(null); setHeroImageFile(null); setLogoImageFile(null);
        refreshTranslations();
        fetchData();
    } catch (err) {
        console.error(err);
        alert(t.admin?.common?.error || 'Erreur lors de la sauvegarde.');
    }
  };

  const handleSaveSettings = async (e, data) => {
    e.preventDefault();
    try {
        await fetch(`${API_BASE}/settings`, { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify(data) 
        });
        alert(t.admin?.common?.success || 'Enregistré !');
        fetchData();
    } catch (err) {
        console.error(err);
        alert(t.admin?.common?.error || 'Erreur lors de la sauvegarde.');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  
  const handleFileDrop = (e, type) => {
      e.preventDefault(); e.stopPropagation(); setDragActive(false);
      if (e.dataTransfer.files[0]) handleSettingsFile(e.dataTransfer.files[0], type);
  }

  const handleSettingsFile = (file, type) => {
      const url = URL.createObjectURL(file);
      if (type === 'about') { setAboutImageFile(file); setAboutInfo(prev => ({...prev, about_image: url})); }
      if (type === 'hero') { setHeroImageFile(file); setAboutInfo(prev => ({...prev, hero_image: url})); }
      if (type === 'logo') { setLogoImageFile(file); setAboutInfo(prev => ({...prev, logo_image: url})); }
  };

  const renderContact = () => (
    <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl p-10 border border-slate-200">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-0.5 bg-brand-600"></div>
          <span className="text-xs font-black uppercase tracking-[0.3em] text-brand-600">Paramètres</span>
        </div>
        <h3 className="text-3xl font-black text-slate-900 font-serif italic">Informations de Contact</h3>
      </div>

      <form onSubmit={(e) => handleSaveSettings(e, contactInfo)} className="space-y-6">
        {[
            { label: t.contact.phone, icon: Phone, key: 'phone' },
            { label: t.contact.email, icon: Mail, key: 'email', type: 'email' },
            { label: t.booking.address, icon: Clock, key: 'address' },
            { label: t.contact.hours, icon: Clock, key: 'hours' },
            { label: 'Instagram', icon: Instagram, key: 'instagram' },
            { label: 'Facebook', icon: Facebook, key: 'facebook' }
        ].map(field => (
            <div key={field.key}>
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">
                  {field.label}
                </label>
                <div className="relative">
                    <field.icon className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                    <input
                        type={field.type || 'text'}
                        value={contactInfo[field.key]}
                        onChange={(e) => setContactInfo({...contactInfo, [field.key]: e.target.value})}
                        className="pl-12 w-full rounded-2xl border-2 border-slate-200 p-3 focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none font-medium"
                    />
                </div>
            </div>
        ))}
        <button type="submit" className="w-full group inline-flex items-center justify-center bg-brand-600 text-white px-10 py-4 rounded-full font-black text-lg hover:bg-brand-700 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-brand-500/40 border-b-4 border-brand-800 active:translate-y-0 active:border-b-0">
          <Save className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform" /> 
          {t.admin?.services?.save || 'Enregistrer'}
        </button>
      </form>
    </div>
  );

  const renderAbout = () => (
    <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl p-10 border border-slate-200">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-0.5 bg-brand-600"></div>
          <span className="text-xs font-black uppercase tracking-[0.3em] text-brand-600">Contenu</span>
        </div>
        <h3 className="text-3xl font-black text-slate-900 font-serif italic">À Propos & Images</h3>
      </div>

      <form onSubmit={handleSaveAbout} className="space-y-10">
        <div className="grid md:grid-cols-3 gap-6">
            {[
                { label: t.admin?.about?.logo || 'Logo', key: 'logo', stateKey: 'logo_image', fileRef: logoFileInputRef, setFile: setLogoImageFile },
                { label: t.admin?.about?.hero || 'Hero Image', key: 'hero', stateKey: 'hero_image', fileRef: heroFileInputRef, setFile: setHeroImageFile },
                { label: t.admin?.about?.aboutImg || 'About Image', key: 'about', stateKey: 'about_image', fileRef: fileInputRef, setFile: setAboutImageFile },
            ].map(block => (
                <div key={block.key} className="space-y-3">
                    <label className="block text-sm font-black uppercase tracking-wider text-slate-700">{block.label}</label>
                    <div 
                        className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all cursor-pointer h-48 flex flex-col items-center justify-center group ${dragActive ? 'border-brand-500 bg-brand-50' : 'border-slate-300 hover:border-brand-400 hover:bg-slate-50'}`}
                        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag}
                        onDrop={(e) => handleFileDrop(e, block.key)}
                        onClick={() => block.fileRef.current.click()}
                    >
                        <input ref={block.fileRef} type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files[0] && handleSettingsFile(e.target.files[0], block.key)} />
                        {aboutInfo[block.stateKey] ? (
                            <img src={getImageUrl(aboutInfo[block.stateKey])} alt="Preview" className="h-full w-full object-contain rounded-xl" />
                        ) : (
                            <>
                              <UploadCloud className="h-12 w-12 text-slate-300 mb-3 group-hover:text-brand-500 transition-colors" />
                              <p className="text-sm text-slate-500 font-medium">Glisser ou cliquer</p>
                            </>
                        )}
                        {aboutInfo[block.stateKey] && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); setAboutInfo(prev => ({...prev, [block.stateKey]: ''})); block.setFile(null); }} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 rounded-full p-2 shadow-lg text-white transition-colors">
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>

        <div className="space-y-6 pt-8 border-t-2 border-slate-100">
          <h3 className="text-2xl font-black text-slate-900 font-serif italic mb-6">
            {t.admin?.about?.titleTranslations || 'Traductions du texte À Propos'}
          </h3>
          <div className="grid gap-6">
              <div>
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">🇫🇷 Français</label>
                <textarea
                    value={aboutTexts.fr}
                    onChange={(e) => setAboutTexts({...aboutTexts, fr: e.target.value})}
                    className="w-full rounded-2xl border-2 border-slate-200 p-4 min-h-[140px] focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none resize-none font-medium"
                    placeholder="Présentation en français..."
                />
              </div>
              <div>
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">🇬🇧 English</label>
                <textarea
                    value={aboutTexts.en}
                    onChange={(e) => setAboutTexts({...aboutTexts, en: e.target.value})}
                    className="w-full rounded-2xl border-2 border-slate-200 p-4 min-h-[140px] focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none resize-none font-medium"
                    placeholder="About text in english..."
                />
              </div>
              <div>
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">🇵🇹 Português</label>
                <textarea
                    value={aboutTexts.pt}
                    onChange={(e) => setAboutTexts({...aboutTexts, pt: e.target.value})}
                    className="w-full rounded-2xl border-2 border-slate-200 p-4 min-h-[140px] focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none resize-none font-medium"
                    placeholder="Sobre nós em português..."
                />
              </div>
          </div>
        </div>

        <button type="submit" className="w-full group inline-flex items-center justify-center bg-brand-600 text-white px-10 py-5 rounded-full font-black text-lg hover:bg-brand-700 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-brand-500/40 border-b-4 border-brand-800 active:translate-y-0 active:border-b-0">
          <Save className="h-6 w-6 mr-3 group-hover:scale-110 transition-transform" /> 
          {t.admin?.about?.saveAll || 'Tout enregistrer'}
        </button>
      </form>
    </div>
  );

  const renderTranslations = () => (
      <div className="max-w-5xl mx-auto space-y-6">
          <div className="bg-white p-8 rounded-3xl shadow-xl flex flex-col md:flex-row gap-6 justify-between items-center border border-slate-200">
              <div className="flex gap-4 w-full md:w-auto flex-wrap">
                  <select value={editingLang} onChange={(e) => setEditingLang(e.target.value)} className="bg-slate-50 border-2 border-slate-200 rounded-2xl py-3 px-4 focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none font-bold">
                      <option value="fr">🇫🇷 Français</option>
                      <option value="en">🇬🇧 English</option>
                      <option value="pt">🇵🇹 Português</option>
                  </select>
                  <div className="relative">
                    <select value={editingSection} onChange={(e) => setEditingSection(e.target.value)} className="bg-slate-50 border-2 border-slate-200 rounded-2xl py-3 px-4 pr-10 appearance-none focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none font-bold">
                        {['nav', 'hero', 'services', 'rates', 'reviews', 'booking', 'about', 'gallery', 'contact', 'auth', 'profile', 'admin'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-4 h-5 w-5 text-slate-400 pointer-events-none" />
                  </div>
              </div>
              <button 
                  onClick={async () => {
                     await fetch(`${API_BASE}/translations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lang: editingLang, updates: translationValues }) });
                     refreshTranslations();
                     alert(t.admin?.translations?.success || 'Traductions mises à jour !');
                  }}
                  className="px-8 py-3 bg-brand-600 text-white rounded-full hover:bg-brand-700 flex items-center justify-center font-black shadow-lg hover:-translate-y-0.5 transition-all"
              >
                  <Save className="h-5 w-5 mr-2" /> {t.admin?.translations?.save || 'Sauvegarder'}
              </button>
          </div>
          <div className="bg-white rounded-3xl shadow-xl p-8 grid gap-6 border border-slate-200">
              {Object.entries(translationValues).map(([key, val]) => (
                  <div key={key}>
                      <label className="block text-xs font-mono text-slate-400 mb-2 uppercase tracking-wider">{key.replace(`${editingSection}.`, '')}</label>
                      <textarea 
                          value={val}
                          onChange={(e) => setTranslationValues(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-full rounded-2xl border-2 border-slate-200 shadow-sm min-h-[50px] py-3 px-4 text-sm focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none font-medium"
                          rows={val.length > 50 ? 3 : 1}
                      />
                  </div>
              ))}
          </div>
      </div>
  );

  const renderAccount = () => (
    <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl p-10 border border-slate-200">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-0.5 bg-brand-600"></div>
          <span className="text-xs font-black uppercase tracking-[0.3em] text-brand-600">Sécurité</span>
        </div>
        <h3 className="text-3xl font-black text-slate-900 font-serif italic">Modifier le mot de passe</h3>
      </div>

      {passwordMessage && (
        <div className={`mb-8 p-5 rounded-2xl flex items-center font-bold ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-700 border-2 border-green-200' : 'bg-red-50 text-red-700 border-2 border-red-200'}`}>
          {passwordMessage.type === 'success' ? <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" /> : <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />}
          {passwordMessage.text}
        </div>
      )}

      <form onSubmit={async (e) => {
          e.preventDefault();
          if (passwordData.newPassword !== passwordData.confirmPassword) return setPasswordMessage({ type: 'error', text: t.admin?.account?.errorMatch || 'Mots de passe différents' });
          try {
            const res = await fetch(`${API_BASE}/auth/update-password`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id, newPassword: passwordData.newPassword })
            });
            if(!res.ok) throw new Error();
            setPasswordMessage({ type: 'success', text: t.admin?.account?.success || 'Succès' });
            setPasswordData({ newPassword: '', confirmPassword: '' });
          } catch(e) { setPasswordMessage({ type: 'error', text: 'Erreur' }); }
      }} className="space-y-6">
        <div>
          <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">
            {t.admin?.account?.newPassword || 'Nouveau mot de passe'}
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
            <input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} className="pl-12 w-full rounded-2xl border-2 border-slate-200 p-3 focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none font-medium" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">
            {t.admin?.account?.confirmPassword || 'Confirmer le mot de passe'}
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
            <input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} className="pl-12 w-full rounded-2xl border-2 border-slate-200 p-3 focus:ring-4 focus:ring-brand-200 focus:border-brand-600 outline-none font-medium" />
          </div>
        </div>
        <button type="submit" className="w-full group inline-flex items-center justify-center bg-brand-600 text-white px-10 py-4 rounded-full font-black text-lg hover:bg-brand-700 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-brand-500/40 border-b-4 border-brand-800 active:translate-y-0 active:border-b-0">
          <Save className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform" /> 
          {t.admin?.account?.save || 'Enregistrer'}
        </button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white pb-16">
      {/* En-tête Admin avec dégradé */}
      <div className="relative py-12 px-4 bg-gradient-to-br from-slate-900 via-brand-900 to-slate-800 text-white overflow-hidden mb-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAzLTRzMyAyIDMgNHYyYzAgMi0yIDQtMyA0cy0zLTItMy00di0yem0wLTMwYzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNFY0ek0wIDM0YzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNHYtMnptMC0zMGMwLTIgMi00IDMtNHMzIDIgMyA0djJjMCAyLTIgNC0zIDRzLTMtMi0zLTRWNHoiLz48L2c+PC9nPjwvc3ZnPg==')] animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-1.5 mb-4 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-6">
            <Shield className="h-4 w-4 mr-2 text-brand-300" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-50">Administration</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-white mb-2 font-serif italic tracking-tight">
            Panneau d'Administration
          </h2>
          <p className="text-lg text-slate-200 font-medium opacity-90">
            Gérez votre site en toute simplicité
          </p>
        </div>
      </div>

      {/* Navigation par onglets améliorée */}
      <div className="bg-white shadow-xl border-y-2 border-slate-100 sticky top-16 z-40 mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-start overflow-x-auto no-scrollbar">
            <nav className="flex space-x-2 py-2" aria-label="Tabs">
              {['bookings', 'services', 'gallery', 'contact', 'about', 'account', 'translations', 'stats'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`${
                    activeTab === tab 
                      ? 'bg-brand-600 text-white shadow-lg' 
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                  } whitespace-nowrap px-6 py-3 rounded-full font-black text-sm transition-all duration-200 hover:-translate-y-0.5`}
                >
                  {t.admin?.tabs?.[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTab === 'bookings' && <AdminBookings bookings={bookings} t={t} lang={lang} onConfirm={handleConfirmBooking} onDelete={(id) => requestDelete(id, 'bookings')} onRefresh={fetchData} />}
        {activeTab === 'services' && <AdminServices services={services} t={t} onSave={handleSaveService} onDelete={(id) => requestDelete(id, 'services')} onRefresh={fetchData} />}
        {activeTab === 'gallery' && <AdminGallery items={galleryItems} t={t} onUpload={handleGalleryUpload} onDelete={(id) => requestDelete(id, 'gallery')} />}
        {activeTab === 'contact' && renderContact()}
        {activeTab === 'about' && renderAbout()}
        {activeTab === 'account' && renderAccount()}
        {activeTab === 'translations' && renderTranslations()}
        {activeTab === 'stats' && <AdminStats t={t} />}
      </div>

      <DeleteConfirmationModal isOpen={!!deleteConfirmation} onClose={() => setDeleteConfirmation(null)} onConfirm={confirmDelete} t={t} />
    </div>
  );
}